import { gatewayConfig } from './gateway-config.js';
import { createGatewayHelpers, isLocalDev } from './gateway-url.js';
// xapi backend integration layer.
// Pure ES module: exports XApi singleton; the IIFE just scopes internal helpers.
//
// Configuration: read directly from `import.meta.env.VITE_*` at build time. Per-env values
// come from the matching `.env.production` / `.env.staging` file consumed during `npm run
// build`. There is no runtime override — each environment gets its own build artefact.

import { XEnv } from './data.jsx';
import {
  clearAuthRefreshBlock,
  isAuthRefreshBlocked,
  refreshAccessTokenShared,
} from './auth-refresh.js';
import {
  buildWsCredentialProtocols,
  createEphemeralCredentialStore,
  isFullAccountSession,
  sessionCredentialIdentity,
} from './api/ephemeral-credentials.js';

const detailPeriodQuery = ({ days = 30, from, to } = {}) => new URLSearchParams(from || to ? { ...(from && { from }), ...(to && { to }) } : { days: String(days) });

export const XApi = (() => {
  // -------- Config (build-time, baked from VITE_* env vars) --------
  // Defaults match the local-dev backend so `npm run dev` works with no .env file.
  // In dev, capabilityBase defaults to '' so requests go to /v1/actions same-origin and hit
  // the Vite proxy (vite.config.js → 3002). For prod set VITE_CAPABILITY_URL to the absolute
  // action-service URL.
  const cfg = {
    ...gatewayConfig,
    apiBase:          import.meta.env.VITE_API_URL                  || '/api',
    capabilityBase:   import.meta.env.VITE_CAPABILITY_URL           || '',
    aiGatewayBase:    import.meta.env.VITE_AI_GATEWAY_URL            || (import.meta.env.DEV ? 'http://localhost:3010' : 'https://ai.xapi.to'),
    // x402 wallet payment (Base mainnet defaults)
    x402Enabled:      import.meta.env.VITE_X402_PAYMENT === 'true' || import.meta.env.DEV,
    x402ChainId:      Number(import.meta.env.VITE_X402_CHAIN_ID     || 8453),
    x402Network:      import.meta.env.VITE_X402_NETWORK             || 'eip155:8453',
    x402Stablecoin:   import.meta.env.VITE_X402_STABLECOIN          || '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    x402CheckoutPath: import.meta.env.VITE_X402_PAYMENT_CHECKOUT_URL|| '/payment/checkout',
    // B402 (Binance OnchainPay) — same /payment/* endpoints, provider=b402, BSC mainnet.
    b402ChainId:      Number(import.meta.env.VITE_B402_CHAIN_ID     || 56),
    b402Network:      import.meta.env.VITE_B402_NETWORK             || 'eip155:56',
    x402MinUsd:       Number(import.meta.env.VITE_MIN_PAYMENT_USD   || 0.01),
    x402MaxUsd:       Number(import.meta.env.VITE_MAX_PAYMENT_USD   || 100000),
  };

  const { buildGatewayUrl, buildWsGatewayUrl, gatewayHost, gatewaySuffix } = createGatewayHelpers(cfg);

  // Browser WebSocket APIs cannot set Authorization headers. Carry the
  // short-lived credential as a requested subprotocol so it is not exposed in
  // the URL, browser history, proxy access logs, or connection error messages.
  // The gateway authenticates xapi-key.* and only echoes the non-secret marker.
  const buildWsAuthProtocols = (token, protocols = []) => {
    const publicProtocols = (Array.isArray(protocols) ? protocols : [protocols])
      .map((protocol) => String(protocol || '').trim())
      .filter((protocol, index, all) =>
        protocol && !protocol.startsWith('xapi-key.') && all.indexOf(protocol) === index,
      );
    if (!publicProtocols.length) publicProtocols.push('xapi-ws-v1');
    if (token) publicProtocols.push(`xapi-key.${token}`);
    return publicProtocols;
  };

  // URL slug for an API service. Backend's resolveIdOrHostToId accepts either UUID or host
  // prefix (e.g. "twitter" matches twitter.localhost or twitter.p.xapi.to). Prefer the host
  // prefix because it gives readable URLs like /apis/twitter instead of /apis/<uuid>.
  // DIRECT-mode services without a host fall back to UUID.
  const apiSlug = (api) => {
    if (!api) return '';
    const host = api.host || '';
    const prefix = host.split('.')[0].split(':')[0];
    return prefix || api.id || '';
  };

  // -------- Tokens (same localStorage keys as xapi-new for cross-app compatibility) --------
  const ACCESS_KEY = 'xapi_access_token';
  const REFRESH_KEY = 'xapi_refresh_token';
  const ephemeralCredentials = createEphemeralCredentialStore();
  const credentialRequests = new Map();
  // Plaintext API key stash — sessionStorage so it dies with the tab. Only consumed
  // by the OAuth "enable-on-legacy-key" auto-upgrade flow. Same key name as xapi-new
  // (`xapi_user_key`) so both apps can share the stash in the same session.
  const USER_KEY_STASH = 'xapi_user_key';
  const userKeyStash = {
    set: (k) => { try { sessionStorage.setItem(USER_KEY_STASH, k); } catch {} },
    get: () => { try { return sessionStorage.getItem(USER_KEY_STASH); } catch { return null; } },
    clear: () => { try { sessionStorage.removeItem(USER_KEY_STASH); } catch {} },
  };
  const tokens = {
    getAccess: () => { try { return localStorage.getItem(ACCESS_KEY); } catch { return null; } },
    getRefresh: () => { try { return localStorage.getItem(REFRESH_KEY); } catch { return null; } },
    set: (a, r) => {
      try {
        const previousOwner = sessionCredentialIdentity(localStorage.getItem(ACCESS_KEY))?.owner;
        const nextOwner = sessionCredentialIdentity(a)?.owner;
        const ownerChanged = previousOwner !== nextOwner;
        if (ownerChanged) ephemeralCredentials.clear();
        localStorage.setItem(ACCESS_KEY, a);
        if (r) localStorage.setItem(REFRESH_KEY, r);
        else if (ownerChanged) localStorage.removeItem(REFRESH_KEY);
      } catch {}
    },
    clear: () => {
      try { localStorage.removeItem(ACCESS_KEY); localStorage.removeItem(REFRESH_KEY); } catch {}
      ephemeralCredentials.clear();
      credentialRequests.clear();
      // Drop the stashed plaintext too — logout means the in-session "I recently
      // typed/created this key" trust signal is gone.
      try { sessionStorage.removeItem(USER_KEY_STASH); } catch {}
    },
  };

  const fullSessionCredentialOwner = () => {
    const identity = sessionCredentialIdentity(tokens.getAccess());
    if (!identity) throw new Error('A signed-in account session is required');
    if (!isFullAccountSession(identity.loginMethod)) {
      throw new Error('A full account login is required; API Key login cannot mint browser credentials');
    }
    return identity.owner;
  };

  const coalesceCredentialRequest = (key, request) => {
    const existing = credentialRequests.get(key);
    if (existing) return existing;
    const pending = Promise.resolve()
      .then(request)
      .finally(() => {
        if (credentialRequests.get(key) === pending) credentialRequests.delete(key);
      });
    credentialRequests.set(key, pending);
    return pending;
  };

  // -------- URL bootstrap: consume tokens / route hints from query before app renders --------
  // Backend OAuth callbacks land us on the SPA with query params we need to honor:
  //   ?accessToken=...&refreshToken=...   ← Twitter / Chain-connect callback
  //   ?oauth=success|error                ← per-key OAuth provider callback
  // Stripe return parameters belong to api/bootstrap.js and the payment return
  // handler. Leave them in the URL until the router has captured them.
  //   ?path=<page>&...                    ← optional deep link
  // The v2 router reads localStorage, so we both persist tokens AND seed the saved route
  // so the matching page can pick up the residual query when it mounts.
  try {
    if (typeof window !== 'undefined' && window.location && window.location.search) {
      const u = new URL(window.location.href);
      const sp = u.searchParams;
      const at = sp.get('accessToken'), rt = sp.get('refreshToken');
      if (at) {
        tokens.set(at, rt);
        sp.delete('accessToken'); sp.delete('refreshToken');
      }
      const seedRoute = (page, params = {}) => {
        try {
          const merged = { page, params: { ...(params), _from: 'callback' } };
          localStorage.setItem('xapi.route', JSON.stringify(merged));
        } catch {}
      };
      if (sp.get('oauth')) seedRoute('console', { tab: 'keys', oauth: sp.get('oauth') });
      else if (sp.get('claim')) seedRoute('claim', { sessionId: sp.get('claim') });
      const explicit = sp.get('path');
      if (explicit) {
        const params = {};
        sp.forEach((v, k) => { if (k !== 'path') params[k] = v; });
        seedRoute(explicit, params);
      }
      // Strip the consumed query so refreshes don't re-trigger.
      const consumed = ['accessToken','refreshToken','oauth','claim','path'];
      let touched = false;
      consumed.forEach(k => { if (sp.has(k)) { sp.delete(k); touched = true; } });
      if (touched) {
        const newSearch = sp.toString();
        const newUrl = u.pathname + (newSearch ? '?' + newSearch : '') + u.hash;
        try { history.replaceState(null, '', newUrl); } catch {}
      }
    }
  } catch {}

  // services.js 与 src/api/client.js 共享同一个 refresh single-flight/熔断器。
  const doRefresh = () =>
    refreshAccessTokenShared({ apiBase: cfg.apiBase, tokenStore: tokens });

  const apiErrorMessage = (data, fallback) => {
    const payload =
      data && typeof data.message === 'object' && data.message !== null
        ? data.message
        : data;
    const rawMessage =
      payload && (payload.message || payload.error)
        ? payload.message || payload.error
        : data && (data.message || data.error)
          ? data.message || data.error
          : fallback;
    const base = Array.isArray(rawMessage) ? rawMessage.join('; ') : String(rawMessage || fallback);
    const issues = Array.isArray(payload?.issues)
      ? payload.issues
      : Array.isArray(data?.issues)
        ? data.issues
        : [];
    if (!issues.length) return base;
    const details = issues
      .slice(0, 4)
      .map((issue) => {
        const location = issue.path ? `${issue.path}: ` : '';
        return `${location}${issue.message || issue.code || 'validation issue'}`;
      })
      .join('; ');
    const suffix = issues.length > 4 ? `; +${issues.length - 4} more` : '';
    return `${base}: ${details}${suffix}`;
  };

  // -------- Generic fetch wrapper with auth + 401 refresh --------
  // Returns { ok, status, data, headers, raw } or throws on network error.
  // opts: { method, body, headers, params, auth='auto' | 'always' | 'none', raw=false, signal }
  async function fetchJson(path, opts = {}) {
    let url = path.startsWith('http') ? path : cfg.apiBase + path;
    // 文档注释一直声称支持 params，但从未实现 —— 调用方传 {page,pageSize} 会被静默
    // 丢弃（列表卡默认页大小、翻页无效、时间窗切换失灵），且完全无报错。补上实现。
    if (opts.params && typeof opts.params === 'object') {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(opts.params)) {
        if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
      }
      const q = qs.toString();
      if (q) url += (url.includes('?') ? '&' : '?') + q;
    }
    const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
    const auth = opts.auth || 'auto';
    if (auth !== 'none') {
      let t = tokens.getAccess();
      // `auth: always` means the operation requires a real session, not that a
      // localStorage mirror must already exist. OAuth sessions also live in
      // HttpOnly cookies; restore the access token from that cookie first.
      if (!t && auth === 'always' && !path.includes('/auth/refresh')) {
        try { t = await doRefresh(); } catch { /* normalized below */ }
      }
      if (t) headers.Authorization = `Bearer ${t}`;
      else if (auth === 'always') throw new Error('not authenticated');
    }
    const init = {
      method: opts.method || 'GET',
      credentials: 'include',
      headers,
      signal: opts.signal,
    };
    if (opts.body !== undefined && opts.body !== null) {
      init.body = typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body);
    }

    let res;
    try { res = await fetch(url, init); }
    catch (e) {
      // Preserve AbortError so callers can distinguish user-cancelled requests.
      if (e && (e.name === 'AbortError' || e.code === 20)) throw e;
      const wrapped = new Error('network: ' + (e && e.message ? e.message : e));
      wrapped.cause = e;
      throw wrapped;
    }

    // Only skip refresh on the refresh endpoint itself (avoid infinite loop).
    // /auth/me, /auth/logout etc. should still get a refresh+retry on 401.
    if (res.status === 401 && auth !== 'none' && !opts._retried && !path.includes('/auth/refresh')) {
      let refreshed = false;
      try { await doRefresh(); refreshed = true; } catch { /* swallow refresh failure; fall through */ }
      if (refreshed) return fetchJson(path, { ...opts, _retried: true }); // recursive call's error MUST bubble
    }

    let data = null;
    const ct = res.headers.get('content-type') || '';
    if (opts.raw) {
      data = res;
    } else if (ct.includes('application/json')) {
      try { data = await res.json(); } catch { data = null; }
    } else {
      try { data = await res.text(); } catch { data = null; }
    }

    if (!res.ok) {
      const err = new Error(apiErrorMessage(data, res.statusText || ('HTTP ' + res.status)));
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return { ok: true, status: res.status, data, headers: res.headers };
  }

  // -------- Auth service --------
  const authService = {
    async register(referralCode) {
      const r = await fetchJson('/auth/register', { method: 'POST', auth: 'none', body: { referralCode } });
      return r.data;
    },
    async loginWithApiKey(apiKey) {
      const r = await fetchJson('/auth/login/apikey', { method: 'POST', auth: 'none', body: { apiKey } });
      const { accessToken, refreshToken, user } = r.data;
      if (accessToken) tokens.set(accessToken, refreshToken);
      return { user, accessToken, refreshToken };
    },
    async getMe() {
      const r = await fetchJson('/auth/me', { auth: 'always' });
      return r.data;
    },
    async logout() {
      try { await fetchJson('/auth/logout', { auth: 'auto' }); } catch {}
      tokens.clear();
      clearBlock();
    },
    async getTwitterAuthorizeUrl(referralCode) {
      const q = referralCode ? `?referralCode=${encodeURIComponent(referralCode)}` : '';
      const r = await fetchJson('/auth/twitter/authorize' + q, { auth: 'none' });
      return r.data;
    },
    async getTwitterLinkUrl() {
      const r = await fetchJson('/auth/twitter/link', { auth: 'always' });
      return r.data;
    },
  };

  // -------- API Key service --------
  const keyService = {
    async list() { const r = await fetchJson('/keys', { auth: 'always' }); return r.data || []; },
    async create(name) { const r = await fetchJson('/keys', { method: 'POST', auth: 'always', body: { name } }); return r.data; },
    async delete(id) { await fetchJson(`/keys/${id}`, { method: 'DELETE', auth: 'always' }); return { id }; },
    async getSummary() { const r = await fetchJson('/keys/usage/summary', { auth: 'always' }); return r.data; },
    async getLimit(id) { const r = await fetchJson(`/keys/${id}/limit`, { auth: 'always' }); return r.data; },
    async setLimit(id, balanceLimit) {
      const r = await fetchJson(`/keys/${id}/limit`, { method: 'PUT', auth: 'always', body: { balanceLimit: String(balanceLimit) } });
      return r.data;
    },
    async removeLimit(id) {
      const r = await fetchJson(`/keys/${id}/limit`, { method: 'DELETE', auth: 'always' });
      return r.data;
    },
    async resetUsed(id) {
      const r = await fetchJson(`/keys/${id}/limit/reset`, { method: 'POST', auth: 'always' });
      return r.data;
    },
    async updateAllowSandbox(id, v) {
      const r = await fetchJson(`/keys/${id}`, { method: 'PATCH', auth: 'always', body: { allowSandbox: !!v } });
      return r.data;
    },
    async updateAllowRegister(id, v) {
      const r = await fetchJson(`/keys/${id}/allow-register`, { method: 'PATCH', auth: 'always', body: { allowRegister: !!v } });
      return r.data;
    },
    // Agent 接管：选择性程序化权限 scopes（含 service:create 时后端会同步 allowRegister）。
    async updateScopes(id, scopes) {
      const r = await fetchJson(`/keys/${id}/scopes`, { method: 'PATCH', auth: 'always', body: { scopes: scopes || [] } });
      return r.data;
    },
    async updateWorkerAccess(id, mode, workerIds) {
      const r = await fetchJson(`/keys/${id}/worker-access`, {
        method: 'PATCH',
        auth: 'always',
        body: { mode, workerIds: workerIds || [] },
      });
      return r.data;
    },
    async updateIpAllowlist(id, allowedCidrs) {
      const r = await fetchJson(`/keys/${id}/ip-allowlist`, {
        method: 'PUT', auth: 'always', body: { allowedCidrs: allowedCidrs || [] },
      });
      return r.data;
    },
    // Upgrade a legacy key (no oauthPublicKey stored yet) so it can bind OAuth
    // providers. Requires the plaintext key — server derives X25519 pubkey
    // and saves it. Only useful for keys created before the OAuth feature
    // shipped; new keys already have it set at create time.
    async enableOAuth(id, plaintextKey) {
      const r = await fetchJson(`/keys/${id}/enable-oauth`, {
        method: 'POST', auth: 'always', body: { plaintextKey },
      });
      return r.data;
    },
    // Return an endpoint-bound browser credential. Immediate reconnects and page
    // reloads reuse the current tab's credential only until latestStartAt, after
    // which a new connection would no longer receive the endpoint's full duration.
    async getWsToken(endpointId) {
      if (!endpointId) throw new Error('endpointId is required');
      const owner = fullSessionCredentialOwner();
      const cached = ephemeralCredentials.getWs(owner, endpointId);
      const now = Date.now();
      if (
        cached?.token &&
        new Date(cached.expiresAt).getTime() > now &&
        new Date(cached.latestStartAt).getTime() > now + 5_000
      ) {
        return cached;
      }
      return coalesceCredentialRequest(`${owner}:ws:${endpointId}`, async () => {
        const r = await fetchJson('/keys/ws-token', { method: 'POST', auth: 'always', body: { endpointId } });
        const credential = r.data;
        if (!credential?.token || !credential?.expiresAt || !credential?.latestStartAt) {
          throw new Error('Invalid ws-token response');
        }
        if (fullSessionCredentialOwner() !== owner) {
          throw new Error('Account session changed while issuing ws-token');
        }
        ephemeralCredentials.setWs(owner, endpointId, credential);
        return credential;
      });
    },
    // Keep the existing Live playground contract while routing every browser
    // caller through the same cached, session-bound credential lifecycle.
    async mintWsToken(endpointId) {
      return keyService.getWsToken(endpointId);
    },
    async getDomainConsoleToken() {
      const owner = fullSessionCredentialOwner();
      const cached = ephemeralCredentials.getDomain(owner);
      if (cached?.token && new Date(cached.expiresAt).getTime() > Date.now() + 120_000) {
        return cached;
      }
      return coalesceCredentialRequest(`${owner}:domain`, async () => {
        const r = await fetchJson('/keys/domain-console-token', {
          method: 'POST', auth: 'always',
        });
        const credential = r.data;
        if (!credential?.token || !credential?.expiresAt) {
          throw new Error('Invalid domain-console-token response');
        }
        if (fullSessionCredentialOwner() !== owner) {
          throw new Error('Account session changed while issuing domain-console-token');
        }
        ephemeralCredentials.setDomain(owner, credential);
        return credential;
      });
    },
  };

  // -------- Usage / Stats service --------
  const usageService = {
    async summary() { const r = await fetchJson('/keys/usage/summary', { auth: 'always' }); return r.data; },
    async daily(days = 7, apiId) {
      const r = await fetchJson(`/keys/usage/daily?days=${days}${apiId ? `&apiId=${encodeURIComponent(apiId)}` : ''}`, { auth: 'always' });
      // Backend (key.service.ts:283) returns { date, calls, cost }; the rest of
      // the frontend (OverviewTab StatCard, Daily-Spend list, chart tooltip)
      // expects the field as `spent`. Normalize once here so the contract is
      // honored regardless of which name the server ever happens to emit.
      const rows = r.data || [];
      return rows.map((d) => ({
        ...d,
        spent: d.spent != null ? d.spent : (d.cost != null ? d.cost : 0),
      }));
    },
    async all(limit = 50, offset = 0, { cursor } = {}) {
      const q = new URLSearchParams({ limit, offset: cursor ? 0 : offset });
      if (cursor) q.set('cursor', cursor);
      const r = await fetchJson(`/keys/usage/all?${q}`, { auth: 'always' });
      return r.data || { total: 0, records: [] };
    },
    // Console Overview 的 Top APIs 卡：按 API 服务聚合的调用量/成本（降序）。
    async byApi(days = 30) {
      const r = await fetchJson(`/keys/usage/by-api?days=${days}`, { auth: 'always' });
      return r.data || [];
    },
    async detail(id) {
      // 单条 UsageRecord 详情（用量明细 drawer），含 timing scalar 拆分（dnsMs/tcpMs/tlsMs/reusedConnection）。
      const r = await fetchJson(`/keys/usage/${encodeURIComponent(id)}`, { auth: 'always' });
      return r.data;
    },
    async forKey(id, limit = 50, offset = 0) {
      const r = await fetchJson(`/keys/${id}/usage?limit=${limit}&offset=${offset}`, { auth: 'always' });
      return r.data || { total: 0, records: [] };
    },
  };

  // -------- AI Logs service (Console > AI Logs) --------
  // Endpoints: GET /statistics/ai/logs                  list (cursor pagination)
  //            GET /statistics/ai/logs/timeseries       chart data (UserAiUsageDaily-backed)
  //            GET /statistics/ai/logs/:id              single row detail
  //            GET /statistics/ai/logs/facets           apiKeys / providers / models dropdowns
  // All four are JWT-only. Server filters by apiKey.userId so a stolen cursor can't escape the user's scope.
  const aiLogsService = {
    async list({ from, to, apiKeyId, apiServiceId, modelName, endpointId, status, errorOrigin, cursor, limit = 50 } = {}) {
      const q = new URLSearchParams();
      if (from) q.set('from', from);
      if (to) q.set('to', to);
      if (apiKeyId) q.set('apiKeyId', apiKeyId);
      if (apiServiceId) q.set('apiServiceId', apiServiceId);
      if (modelName) q.set('modelName', modelName);
      if (endpointId) q.set('endpointId', endpointId);
      if (status) q.set('status', status);
      if (errorOrigin) q.set('errorOrigin', errorOrigin);
      if (cursor) q.set('cursor', cursor);
      q.set('limit', String(limit));
      try {
        const r = await fetchJson(`/statistics/ai/logs?${q}`, { auth: 'always' });
        return r.data || { rows: [], nextCursor: null, hasMore: false };
      } catch (e) {
        if (e && e.status === 403) return { rows: [], nextCursor: null, hasMore: false };
        throw e;
      }
    },
    async timeseries({ from, to, days = 30, apiKeyId, apiServiceId, modelName } = {}) {
      const q = new URLSearchParams();
      if (from && to) { q.set('from', from); q.set('to', to); }
      else q.set('days', String(days));
      if (apiKeyId) q.set('apiKeyId', apiKeyId);
      if (apiServiceId) q.set('apiServiceId', apiServiceId);
      if (modelName) q.set('modelName', modelName);
      try {
        const r = await fetchJson(`/statistics/ai/logs/timeseries?${q}`, { auth: 'always' });
        return r.data || [];
      } catch (e) {
        if (e && e.status === 403) return [];
        throw e;
      }
    },
    async detail(id) {
      const r = await fetchJson(`/statistics/ai/logs/${encodeURIComponent(id)}`, { auth: 'always' });
      return r.data;
    },
    async facets() {
      const EMPTY = { apiKeys: [], providers: [], models: [], endpoints: [] };
      try {
        const r = await fetchJson('/statistics/ai/logs/facets', { auth: 'always' });
        return { ...EMPTY, ...(r.data || {}) };
      } catch (e) {
        if (e && e.status === 403) return EMPTY;
        throw e;
      }
    },
  };

  // -------- Statistics service (project-level + platform) --------
  const statsService = {
    async overview() { const r = await fetchJson('/statistics/overview', { auth: 'always' }); return r.data; },
    async transactions({ page = 1, limit = 20, type, includeSummary = true, refreshSummary = false, includeCount = true, cursor, direction, signal } = {}) {
      const q = new URLSearchParams({ page, limit }); if (type) q.set('type', type);
      q.set('includeSummary', String(includeSummary));
      q.set('refreshSummary', String(refreshSummary));
      q.set('includeCount', String(includeCount));
      if (cursor) q.set('cursor', cursor);
      if (direction) q.set('direction', direction);
      const r = await fetchJson('/statistics/transactions?' + q, { auth: 'always', signal });
      return r.data || { items: [], total: 0 };
    },
    async platform() {
      try { const r = await fetchJson('/statistics/platform', { auth: 'none' }); return r.data; }
      catch { return null; }
    },
    async projectsSummary() {
      try { const r = await fetchJson('/statistics/projects/summary', { auth: 'always' }); return r.data || []; }
      catch { return []; }
    },
    // GET /statistics/api/:apiId/public — public, single API uptime/latency/hits.
    async apiPublic(apiId, days = 30) {
      try { const r = await fetchJson(`/statistics/api/${encodeURIComponent(apiId)}/public?days=${days}`, { auth: 'none' }); return r.data; }
      catch { return null; }
    },
    // GET /statistics/api/:apiId/timeseries — per-day points for sparkline (cached 5m server-side).
    async apiTimeseries(apiId, days = 30) {
      try {
        const r = await fetchJson(`/statistics/api/${encodeURIComponent(apiId)}/timeseries?days=${days}`, { auth: 'none' });
        return r.data || null;
      } catch { return null; }
    },
    // GET /statistics/api/batch/timeseries — chunks to 50 ids per request.
    async timeseriesBatch(apiIds, days = 30) {
      const out = {};
      const ids = (apiIds || []).filter(Boolean);
      if (!ids.length) return out;
      const CHUNK = 50;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        try {
          const r = await fetchJson(`/statistics/api/batch/timeseries?apiIds=${encodeURIComponent(slice.join(','))}&days=${days}`, { auth: 'none' });
          if (r.data && typeof r.data === 'object') Object.assign(out, r.data);
        } catch {}
      }
      return out;
    },
    // GET /statistics/api/batch/public?apiIds=a,b,c — best-effort batch lookup; chunks to 50.
    async publicBatch(apiIds, days = 30) {
      const out = {};
      const ids = (apiIds || []).filter(Boolean);
      if (!ids.length) return out;
      const CHUNK = 50;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        try {
          const r = await fetchJson(`/statistics/api/batch/public?apiIds=${encodeURIComponent(slice.join(','))}&days=${days}`, { auth: 'none' });
          if (r.data && typeof r.data === 'object') Object.assign(out, r.data);
        } catch {}
      }
      return out;
    },
  };

  // -------- Favorites (per-user marketplace bookmarks) --------
  const favoriteService = {
    async list() {
      try { const r = await fetchJson('/api-services/favorites', { auth: 'always' }); return r.data || { items: [], total: 0 }; }
      catch { return { items: [], total: 0 }; }
    },
    async status(apiId) {
      try { const r = await fetchJson(`/api-services/${encodeURIComponent(apiId)}/favorite`, { auth: 'always' }); return !!(r.data && r.data.isFavorited); }
      catch { return false; }
    },
    async add(apiId) {
      const r = await fetchJson(`/api-services/${encodeURIComponent(apiId)}/favorite`, { method: 'POST', auth: 'always' });
      return r.data;
    },
    async remove(apiId) {
      const r = await fetchJson(`/api-services/${encodeURIComponent(apiId)}/favorite`, { method: 'DELETE', auth: 'always' });
      return r.data;
    },
  };

  // -------- Update subscriptions (独立于收藏——订阅后新版本带 changelog 会推邮件) --------
  const subscriptionService = {
    async add(apiId) {
      const r = await fetchJson(`/api-services/${encodeURIComponent(apiId)}/subscribe`, { method: 'POST', auth: 'always' });
      return r.data;
    },
    async remove(apiId) {
      const r = await fetchJson(`/api-services/${encodeURIComponent(apiId)}/subscribe`, { method: 'DELETE', auth: 'always' });
      return r.data;
    },
  };

  // -------- Provider dashboard (服务商收益 + 调用分析) --------
  // Backend mounts these under /provider/dashboard with three guards: JwtAuth + EntityAccount
  // + ProviderWhitelist. Non-provider users get 403 — caller should gate the UI on
  // useAuth().canProvideApis to avoid a confusing forbidden error.
  const providerDashboardService = {
    async overview() {
      try { const r = await fetchJson('/provider/dashboard/overview', { auth: 'always' }); return r.data; }
      catch (e) { if (e && e.status === 403) return null; throw e; }
    },
    // apiId 可选：默认 = 全 provider 汇总，传入 = 仅该 API 的每日趋势
    async earningsTrend({ days = 30, apiId } = {}) {
      const q = new URLSearchParams({ days: String(days) });
      if (apiId) q.set('apiId', apiId);
      try { const r = await fetchJson(`/provider/dashboard/earnings/trend?${q}`, { auth: 'always' }); return r.data || []; }
      catch (e) { if (e && e.status === 403) return []; throw e; }
    },
    async apis({ days = 30 } = {}) {
      try { const r = await fetchJson(`/provider/dashboard/apis?days=${days}`, { auth: 'always' }); return r.data || []; }
      catch (e) { if (e && e.status === 403) return []; throw e; }
    },
    async apiDetail(apiId, { days = 30 } = {}) {
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}?days=${days}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    async trafficAnalysis(apiId, { from, to, granularity = 'day' }) {
      const q = new URLSearchParams({ startDate: from, endDate: to, granularity, comparison: 'optional' });
      const read = () => fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/traffic?${q}`, { auth: 'always' });
      try { return (await read()).data; }
      catch (e) {
        // Old/mixed API replicas reject this one new capability parameter.
        // Retry once with their strict legacy contract; never downgrade a422.
        if (e?.status !== 400 || !Array.isArray(e.data?.message) ||
          e.data.message.length !== 1 || e.data.message[0] !== 'property comparison should not exist') throw e;
        q.delete('comparison');
        return (await read()).data;
      }
    },
    // Exact Revenue window; legacy days-based analytics keep their contract.
    async revenueAnalysis(apiId, { startDate, endDate }) {
      const q = new URLSearchParams({ startDate, endDate });
      const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/revenue?${q}`, { auth: 'always' });
      return r.data;
    },
    async earningsHistory({ page = 1, limit = 20, apiServiceId, startDate, endDate, status, cursor, includeSummary = true } = {}) {
      const q = new URLSearchParams({ page: cursor ? 1 : page, limit });
      if (cursor) q.set('cursor', cursor);
      if (!includeSummary) q.set('includeSummary', 'false');
      if (apiServiceId) q.set('apiServiceId', apiServiceId);
      if (startDate) q.set('startDate', startDate);
      if (endDate) q.set('endDate', endDate);
      if (status) q.set('status', status);
      try { const r = await fetchJson(`/provider/dashboard/earnings/history?${q}`, { auth: 'always' }); return r.data || { items: [], total: 0, totalPages: 0 }; }
      catch (e) { if (e && e.status === 403) return { items: [], total: 0, totalPages: 0 }; throw e; }
    },
    async callers({ days = 30 } = {}) {
      try { const r = await fetchJson(`/provider/dashboard/callers?days=${days}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && e.status === 403) return null; throw e; }
    },
    // Phase 2 — caller insights with firstSeen / lastSeen / status / sparkline
    // apiId 可选：不传 = provider-wide，传 = 限定到该 API 的调用方（修复
    // 跨 API 看到同一份 caller 的 v1 bug）。
    async callerInsights({ days = 30, apiId, from, to } = {}) {
      const q = new URLSearchParams(from || to ? { ...(from && { from }), ...(to && { to }) } : { days: String(days) });
      if (apiId) q.set('apiId', apiId);
      try { const r = await fetchJson(`/provider/dashboard/callers/insights?${q}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && e.status === 403) return null; throw e; }
    },
    async callerPage({ apiId, from, to, view = 'list', limit = 50, cursor, search, status, sort, dir } = {}) {
      const q = new URLSearchParams({ apiId, from, to, view, limit: String(limit) });
      for (const [key, value] of Object.entries({ cursor, search, status, sort, dir })) if (value) q.set(key, value);
      const r = await fetchJson(`/provider/dashboard/callers/insights?${q}`, { auth: 'always' });
      return r.data;
    },
    // 自助视图:当前用户的全局客户代号(跨服务商一致)。{ callerRef, callerHandle }
    async myCallerAlias() {
      try { const r = await fetchJson('/provider/dashboard/me/caller-alias', { auth: 'always' }); return r.data || null; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // D1 — single endpoint drill-down KPI + meta
    async endpointDetail(apiId, endpointId, period = {}) {
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/endpoints/${encodeURIComponent(endpointId)}?${detailPeriodQuery(period)}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // endpoint drill-down — async task funnel (rendered when asyncMode != NONE)
    async endpointTaskStats(apiId, endpointId, period = {}) {
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/endpoints/${encodeURIComponent(endpointId)}/tasks?${detailPeriodQuery(period)}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // endpoint drill-down — per-model AI usage (rendered when isAi)
    async endpointAiUsage(apiId, endpointId, period = {}) {
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/endpoints/${encodeURIComponent(endpointId)}/ai-usage?${detailPeriodQuery(period)}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // endpoint drill-down — WS session stats (rendered when protocol === 'WEBSOCKET')
    async endpointWsSessions(apiId, endpointId, period = {}) {
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/endpoints/${encodeURIComponent(endpointId)}/ws-sessions?${detailPeriodQuery(period)}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // D2 — alert rules CRUD
    async listAlertRules() {
      try { const r = await fetchJson(`/provider/dashboard/alert-rules`, { auth: 'always' }); return r.data || []; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return []; throw e; }
    },
    async createAlertRule(body) {
      const r = await fetchJson(`/provider/dashboard/alert-rules`, { method: 'POST', auth: 'always', body });
      return r.data;
    },
    async updateAlertRule(ruleId, body) {
      const r = await fetchJson(`/provider/dashboard/alert-rules/${encodeURIComponent(ruleId)}`, { method: 'PATCH', auth: 'always', body });
      return r.data;
    },
    async deleteAlertRule(ruleId) {
      const r = await fetchJson(`/provider/dashboard/alert-rules/${encodeURIComponent(ruleId)}`, { method: 'DELETE', auth: 'always' });
      return r.data;
    },
    // D2 — in-app inbox + mark-read
    async listAlertNotifications({ unreadOnly = false, limit = 50 } = {}) {
      const q = new URLSearchParams({ limit: String(limit) });
      if (unreadOnly) q.set('unread', '1');
      try { const r = await fetchJson(`/provider/dashboard/alert-notifications?${q}`, { auth: 'always' }); return r.data || { unreadCount: 0, items: [] }; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return { unreadCount: 0, items: [] }; throw e; }
    },
    async markAlertRead(ids) {
      const r = await fetchJson(`/provider/dashboard/alert-notifications/mark-read`, { method: 'POST', auth: 'always', body: { ids } });
      return r.data;
    },
    // D5 — anomaly inbox + resolve
    async anomalies({ resolved = 'open', apiId, limit = 50 } = {}) {
      const q = new URLSearchParams({ resolved, limit: String(limit) });
      if (apiId) q.set('apiId', apiId);
      try { const r = await fetchJson(`/provider/dashboard/anomalies?${q}`, { auth: 'always' }); return r.data || { unreadCount: 0, items: [] }; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return { unreadCount: 0, items: [] }; throw e; }
    },
    async resolveAnomaly(anomalyId, note) {
      const q = new URLSearchParams();
      if (note) q.set('note', note);
      try { const r = await fetchJson(`/provider/dashboard/anomalies/${encodeURIComponent(anomalyId)}/resolve?${q}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // D4 — single caller drill-down (this API's view of that user)
    async callerDetail(apiId, callerId, period = {}) {
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/callers/${encodeURIComponent(callerId)}?${detailPeriodQuery(period)}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // Phase 2 — single-API timeseries; granularity 'hour' (≤36h) or 'day' (≤90d)
    async apiTimeseries(apiId, { granularity = 'day', from, to, endpointId } = {}) {
      const q = new URLSearchParams({ granularity });
      if (from) q.set('from', from);
      if (to) q.set('to', to);
      if (endpointId) q.set('endpointId', endpointId);
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/timeseries?${q}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // Phase 2 — status-code distribution { '2xx', '3xx', '4xx', '5xx', total }
    async apiStatusCodes(apiId, { from, to, endpointId } = {}) {
      const q = new URLSearchParams();
      if (from) q.set('from', from);
      if (to) q.set('to', to);
      if (endpointId) q.set('endpointId', endpointId);
      const qs = q.toString();
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/status-codes${qs ? '?' + qs : ''}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // Phase 2/3 — provider-scoped call logs (covers all calls, not just AI;
    // includes clientIp / country). Returns { items, nextCursor }. Detail
    // fields are NOT in the list payload — call logDetail(id) for full.
    async apiLogs(apiId, { from, to, limit = 50, cursor, statusBucket, endpointId, country, callerId, method, search, logId, scope, statusCode } = {}) {
      const q = new URLSearchParams({ limit: String(limit) });
      if (from) q.set('from', from);
      if (to) q.set('to', to);
      if (cursor) q.set('cursor', cursor);
      if (statusBucket) q.set('statusBucket', statusBucket);
      if (endpointId) q.set('endpointId', endpointId);
      if (country) q.set('country', country);
      if (callerId) q.set('callerId', callerId);
      if (method) q.set('method', method);
      if (search) q.set('search', search);
      if (logId) q.set('logId', logId);
      if (scope) q.set('scope', scope);
      if (statusCode != null) q.set('statusCode', String(statusCode));
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/logs?${q}`, { auth: 'always' }); return r.data || { items: [], nextCursor: null }; }
      catch (e) { if (!scope && e && (e.status === 403 || e.status === 404)) return { items: [], nextCursor: null }; throw e; }
    },
    // Single log detail (元信息 + hasFullLog；方案A 后不再含 *Trunc 预览)
    async logDetail(logId) {
      try { const r = await fetchJson(`/provider/dashboard/logs/${encodeURIComponent(logId)}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // 完整响应：换取 R2 归档对象的 5min presigned URL（只在 detail.hasFullLog 时调）。
    // 返回 { available, url?, expiresAt?, reason? }；客户端只传 logId，不碰 object key。
    async logFullResponse(logId) {
      try { const r = await fetchJson(`/provider/dashboard/logs/${encodeURIComponent(logId)}/full-response-url`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return { available: false, reason: 'not_found' }; throw e; }
    },
    // Phase A4 — latency breakdown 拆 gateway / connect / upstream / stream 四段
    async apiLatencyBreakdown(apiId, { days = 30, from, to, endpointId } = {}) {
      const q = detailPeriodQuery({ days, from, to });
      if (endpointId) q.set('endpointId', endpointId);
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/latency-breakdown?${q}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // Phase C3 — 按调用方拆解收入（JOIN provider_earnings → users），D1 可加 endpointId
    async apiCallersRevenue(apiId, { days = 30, from, to, limit = 100, endpointId } = {}) {
      const q = detailPeriodQuery({ days, from, to });
      q.set('limit', String(limit));
      if (endpointId) q.set('endpointId', endpointId);
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/callers-revenue?${q}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // Phase C2 — 错误分组：按 (statusCode, endpointId) 聚合 top N 失败
    async apiErrorGroups(apiId, { from, to, endpointId } = {}) {
      const q = new URLSearchParams();
      if (from) q.set('from', from);
      if (to) q.set('to', to);
      if (endpointId) q.set('endpointId', endpointId);
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/error-groups?${q}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // Phase A6 — AI 用量视角（仅含 AI endpoint 的 API 有意义）
    async apiAiUsage(apiId, { days = 30 } = {}) {
      try { const r = await fetchJson(`/provider/dashboard/apis/${encodeURIComponent(apiId)}/ai-usage?days=${days}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
    // Phase A5 — 滚动活跃调用方 1d/7d/30d 窗口
    async activeCallersWindows({ apiId } = {}) {
      const q = new URLSearchParams();
      if (apiId) q.set('apiId', apiId);
      try { const r = await fetchJson(`/provider/dashboard/callers/active-windows?${q}`, { auth: 'always' }); return r.data; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return null; throw e; }
    },
  };

  // -------- Withdrawal (服务商收益提现) --------
  // 提现只动 earningsBalance（服务商 API 收益），不含充值/赠送。人工审批后 CDP 出款。
  const withdrawalService = {
    async create({ amount, toAddress, idempotencyKey } = {}) {
      const r = await fetchJson('/payment/withdraw', {
        method: 'POST', auth: 'always',
        body: { amount, toAddress, ...(idempotencyKey ? { idempotencyKey } : {}) },
      });
      return r.data;
    },
    async listMine() {
      try { const r = await fetchJson('/payment/withdrawals', { auth: 'always' }); return (r.data && r.data.items) || []; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return []; throw e; }
    },
    async cancel(id) {
      const r = await fetchJson(`/payment/withdrawals/${encodeURIComponent(id)}/cancel`, { method: 'POST', auth: 'always' });
      return r.data;
    },
    // 提现规则（最低/最高/单日上限/网络/币种）—— 前端表单展示用
    async config() {
      try { const r = await fetchJson('/payment/withdrawals/config', { auth: 'always' }); return r.data || null; }
      catch { return null; }
    },
  };

  // 收益划转：把服务商收益(earningsBalance) 转入消费余额(balance)。单向不可逆。
  const earningsTransferService = {
    async create({ amount, idempotencyKey } = {}) {
      const r = await fetchJson('/payment/earnings/transfer', {
        method: 'POST', auth: 'always',
        body: { amount, ...(idempotencyKey ? { idempotencyKey } : {}) },
      });
      return r.data;
    },
    async listMine() {
      try { const r = await fetchJson('/payment/earnings/transfers', { auth: 'always' }); return (r.data && r.data.items) || []; }
      catch (e) { if (e && (e.status === 403 || e.status === 404)) return []; throw e; }
    },
    async config() {
      try { const r = await fetchJson('/payment/earnings/transfer/config', { auth: 'always' }); return r.data || null; }
      catch { return null; }
    },
  };

  // -------- Capabilities (cross-API skills via /v1/actions) --------
  const actionsService = {
    async list({ pageSize = 200, source = 'capability' } = {}) {
      const url = `${cfg.capabilityBase}/v1/actions?source=${source}&page_size=${pageSize}`;
      const headers = {};
      const apiKey = XEnv && XEnv.get ? XEnv.get('XAPI_KEY') : null;
      if (apiKey) headers['XAPI-Key'] = apiKey;
      const r = await fetch(url, { headers });
      if (!r.ok) throw new Error('list actions: HTTP ' + r.status);
      const j = await r.json();
      return j.actions || [];
    },
    async detail(id) {
      const headers = { 'Content-Type': 'application/json' };
      const apiKey = XEnv && XEnv.get ? XEnv.get('XAPI_KEY') : null;
      if (apiKey) headers['XAPI-Key'] = apiKey;
      // Batch lookup avoids collisions with collection routes (e.g. "search")
      // and also works with backends that predate the explicit by-id route.
      const r = await fetch(`${cfg.capabilityBase}/v1/actions/batch`, {
        method: 'POST', headers, body: JSON.stringify({ ids: [id] }),
      });
      if (!r.ok) throw new Error('action detail: HTTP ' + r.status);
      const j = await r.json();
      if (!Array.isArray(j?.actions) || !Array.isArray(j?.missing_ids)) {
        throw new Error('action detail: invalid response');
      }
      if (j.missing_ids.includes(id) || !j.actions.length) {
        throw new Error('action detail: HTTP 404');
      }
      // Existing capability pages consume one schema, including when an API
      // exposes several HTTP methods. Keep the established first-entry shape.
      return j.actions[0];
    },
    /**
     * Execute action. If `streaming` true, parses SSE; calls onChunk(piece, raw) per event.
     * Returns { success, data, error?, latency, status, headers, body }.
     */
    async execute({ actionId, input, streaming = false, onChunk, signal, apiKey: apiKeyOverride }) {
      const headers = { 'Content-Type': 'application/json' };
      const apiKey = apiKeyOverride || (XEnv && XEnv.get ? XEnv.get('XAPI_KEY') : null);
      if (apiKey) headers['XAPI-Key'] = apiKey;
      if (streaming) headers['Accept'] = 'text/event-stream';
      const t0 = performance.now();
      const r = await fetch(`${cfg.capabilityBase}/v1/actions/execute`, {
        method: 'POST',
        headers,
        body: JSON.stringify(streaming ? { action_id: actionId, input, stream: true } : { action_id: actionId, input }),
        signal,
      });
      const respHeaders = {}; r.headers.forEach((v, k) => { respHeaders[k] = v; });
      if (streaming && r.body && (r.headers.get('content-type') || '').includes('text/event-stream')) {
        const reader = r.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        let combined = '';
        for (;;) {
          let chunk; try { chunk = await reader.read(); } catch { break; }
          if (chunk.done) break;
          buf += dec.decode(chunk.value, { stream: true });
          let nl;
          while ((nl = buf.indexOf('\n')) !== -1) {
            const line = buf.slice(0, nl).replace(/\r$/, '');
            buf = buf.slice(nl + 1);
            if (!line || line.startsWith(':')) continue;
            if (line.startsWith('data:')) {
              const d = line.slice(5).trim();
              if (!d || d === '[DONE]') continue;
              let parsed; try { parsed = JSON.parse(d); } catch { parsed = d; }
              combined += (typeof parsed === 'string' ? parsed : JSON.stringify(parsed)) + '\n';
              try { onChunk && onChunk(parsed); } catch {}
            }
          }
        }
        return { ok: r.ok, status: r.status, headers: respHeaders, streaming: true, body: combined, latency: Math.round(performance.now() - t0) };
      }
      const ct = r.headers.get('content-type') || '';
      let data; if (ct.includes('application/json')) { try { data = await r.json(); } catch { data = null; } }
      else { try { data = await r.text(); } catch { data = null; } }
      return { ok: r.ok, status: r.status, headers: respHeaders, body: data, latency: Math.round(performance.now() - t0) };
    },
  };

  // -------- Skill marketplace --------
  const skillService = {
    async list({ q, category, tag, page = 1, limit = 24 } = {}) {
      const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (q) qs.set('q', q);
      if (category && category !== 'all') qs.set('category', category);
      if (tag) qs.set('tag', tag);
      const r = await fetchJson(`/skills?${qs}`, { auth: 'none' });
      return r.data || { total: 0, page, limit, items: [] };
    },
    async detail(slug) {
      const r = await fetchJson(`/skills/${encodeURIComponent(slug)}`, { auth: 'none' });
      return r.data;
    },
    async recordInstall(slug) {
      const r = await fetchJson(`/skills/${encodeURIComponent(slug)}/install`, {
        method: 'POST',
        auth: 'none',
      });
      return r.data;
    },
    async mine() {
      try {
        const r = await fetchJson('/skills/mine', { auth: 'always' });
        return r.data || [];
      } catch (e) {
        if (e && e.status === 403) return [];
        throw e;
      }
    },
    async create(payload) {
      const r = await fetchJson('/skills', { method: 'POST', auth: 'always', body: payload });
      return r.data;
    },
    async slugAvailability(slug) {
      const r = await fetchJson(`/skills/${encodeURIComponent(slug)}/availability`, {
        auth: 'always',
      });
      return r.data;
    },
    async previewZip(file) {
      const form = new FormData();
      form.append('file', file);
      return fetchSkillForm('/skills/preview/zip', form);
    },
    async previewGitHub(url) {
      const r = await fetchJson('/skills/preview/github', {
        method: 'POST',
        auth: 'always',
        body: { url },
      });
      return r.data;
    },
    async uploadZip(slug, file, metadata = {}) {
      const form = new FormData();
      form.append('file', file);
      for (const key of ['version', 'name', 'description']) {
        if (metadata[key] != null) form.append(key, String(metadata[key]));
      }
      for (const key of ['categories', 'tags']) {
        if (Array.isArray(metadata[key])) {
          form.append(key, JSON.stringify(metadata[key]));
        }
      }
      return fetchSkillForm(`/skills/${encodeURIComponent(slug)}/uploads`, form);
    },
    async importGitHub(slug, url, metadata = {}) {
      const r = await fetchJson(`/skills/${encodeURIComponent(slug)}/imports/github`, {
        method: 'POST',
        auth: 'always',
        body: { url, ...metadata },
      });
      return r.data;
    },
    async submit(slug, skillVersionId, note, sourceType) {
      const r = await fetchJson(`/skills/${encodeURIComponent(slug)}/submissions`, {
        method: 'POST',
        auth: 'always',
        body: { skillVersionId, note, sourceType },
      });
      return r.data;
    },
    async submission(slug, submissionId) {
      const r = await fetchJson(`/skills/${encodeURIComponent(slug)}/submissions/${encodeURIComponent(submissionId)}`, { auth: 'always' });
      return r.data;
    },
    async agentSpec() {
      const r = await fetchJson('/skills/agent/spec', { auth: 'none' });
      return r.data;
    },
  };

  async function fetchSkillForm(path, formData, retried = false) {
    const call = async () => {
      let t = tokens.getAccess();
      if (!t) {
        try { t = await doRefresh(); } catch { /* normalized below */ }
      }
      if (!t) throw new Error('not authenticated');
      return fetch(cfg.apiBase + path, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${t}` },
        body: formData,
      });
    };
    let res = await call();
    if (res.status === 401 && !retried) {
      await doRefresh();
      res = await call();
    }
    let data = null;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try { data = await res.json(); } catch { data = null; }
    } else {
      try { data = await res.text(); } catch { data = null; }
    }
    if (!res.ok) {
      const err = new Error(apiErrorMessage(data, res.statusText || (`HTTP ${res.status}`)));
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  // -------- x402 wallet payment (delegates to window.XWallet at runtime) --------
  // The wallet stack (wagmi/viem/x402) must be loaded via wallet.jsx since we have no bundler.
  // Provides: isReady() / connect() / disconnect() / address / chainId / switchChain() / paidCheckout()
  const x402Service = {
    isAvailable() { return !!(window.XWallet && window.XWallet.x402Checkout); },
    async checkout(amountUsd, opts = {}) {
      if (!this.isAvailable()) throw new Error('x402 wallet not available — load wallet.jsx with VITE_X402_PAYMENT=true');
      if (!Number.isFinite(amountUsd)) throw new Error('Invalid amount');
      if (amountUsd < cfg.x402MinUsd || amountUsd > cfg.x402MaxUsd) {
        throw new Error(`Amount must be between $${cfg.x402MinUsd} and $${cfg.x402MaxUsd}`);
      }
      const isB402 = opts.provider === 'b402';
      // The wallet adapter handles the 402→sign→retry flow internally.
      return window.XWallet.x402Checkout({
        url: cfg.apiBase + cfg.x402CheckoutPath,
        amountUsd,
        accessToken: tokens.getAccess(),
        provider: isB402 ? 'b402' : 'x402',
        asset: isB402 ? (opts.asset || 'U') : undefined,
        network: isB402 ? cfg.b402Network : cfg.x402Network,
        chainId: isB402 ? cfg.b402ChainId : cfg.x402ChainId,
        stablecoin: isB402 ? undefined : cfg.x402Stablecoin,
      });
    },
  };

  // -------- Agent (AI-assisted API import) --------
  const agentService = {
    async analyze(rawInput) {
      const r = await fetchJson('/api-services/agent/analyze', { method: 'POST', auth: 'always', body: { rawInput } });
      return r.data;
    },
    /**
     * Streaming analyze — mirrors xapi-new's parser (apiService.ts:555–646).
     * SSE blocks are separated by a blank line (\n\n), each containing
     *   event: <name>
     *   data: <json>
     *
     * Events routed:
     *   - progress  → onProgress({ stage, message, tokens? })
     *   - done      → resolve with AgentAnalyzeResponse { sessionId, openApiSpec, extractedInfo, warnings }
     *   - error     → reject with Error(data.message)
     *
     * Handles 401 via single-flight refresh + one retry.
     */
    async analyzeStream(rawInput, onProgress, opts = {}) {
      const url = cfg.apiBase + '/api-services/agent/analyze-stream';
      const callOnce = (token) => fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ rawInput }),
        signal: opts.signal,
      });

      let res = await callOnce(tokens.getAccess());
      if (res.status === 401) {
        const newToken = await doRefresh();
        res = await callOnce(newToken);
      }
      if (!res.ok || !res.body) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); msg = j.message || msg; } catch {}
        throw new Error(msg);
      }

      // Parse SSE: blocks split by blank line (\n\n), each block has event: + data: lines.
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buffer = '';
      for (;;) {
        let chunk;
        try { chunk = await reader.read(); }
        catch (e) {
          if (e && (e.name === 'AbortError' || e.code === 20)) throw e;
          throw new Error('stream read error: ' + (e && e.message ? e.message : e));
        }
        if (chunk.done) break;

        buffer += dec.decode(chunk.value, { stream: true });
        const blocks = buffer.split('\n\n');
        buffer = blocks.pop() || '';   // last (possibly partial) block stays in buffer

        for (const block of blocks) {
          if (!block.trim()) continue;
          const eventMatch = block.match(/^event:\s*(.+)$/m);
          const dataMatch  = block.match(/^data:\s*(.+)$/m);
          if (!eventMatch || !dataMatch) continue;

          const event = eventMatch[1].trim();
          let data;
          try { data = JSON.parse(dataMatch[1]); }
          catch { continue; }

          if (event === 'done') {
            // Final payload; don't wait for stream end.
            return data;
          } else if (event === 'error') {
            throw new Error(data.message || 'Analysis failed');
          } else if (event === 'progress') {
            try { onProgress && onProgress(data); } catch {}
          }
        }
      }
      // Stream ended without 'done' event — something went wrong upstream.
      throw new Error('Stream ended without result');
    },
    async test({ sessionId, endpointIndex, privateHeaders, testParams, testHeaders, testBody }) {
      const r = await fetchJson('/api-services/agent/test', {
        method: 'POST',
        auth: 'always',
        body: { sessionId, endpointIndex, privateHeaders, testParams, testHeaders, testBody },
      });
      return r.data;
    },
    async confirm(payload) {
      const r = await fetchJson('/api-services/agent/confirm', { method: 'POST', auth: 'always', body: payload });
      return r.data;
    },
    // In-editor draft test: fire one SSRF-guarded upstream call from the config
    // the provider is editing (no analyze session). Returns { success, statusCode,
    // latencyMs, responseBody, error }.
    async testConfig({ baseUrl, method, path, authType, privateHeaders, testParams, testHeaders, testBody }) {
      const r = await fetchJson('/api-services/agent/test-config', {
        method: 'POST',
        auth: 'always',
        body: { baseUrl, method, path, authType, privateHeaders, testParams, testHeaders, testBody },
      });
      return r.data;
    },
  };

  // -------- Stripe service --------
  const stripeService = {
    async createSession(amountUsd, { successUrl, cancelUrl } = {}) {
      const origin = window.location.origin;
      const body = {
        amountUsd,
        successUrl: successUrl || `${origin}/?stripe=success&session={CHECKOUT_SESSION_ID}`,
        cancelUrl: cancelUrl || `${origin}/?stripe=cancel`,
      };
      const r = await fetchJson('/payment/stripe/create-session', { method: 'POST', auth: 'always', body });
      return r.data;
    },
    async getSession(sessionId) {
      const r = await fetchJson(`/payment/stripe/session/${sessionId}`, { auth: 'always' });
      return r.data;
    },
    async getAutoRecharge() {
      const r = await fetchJson('/payment/stripe/auto-recharge', { auth: 'always' });
      return r.data;
    },
    async createAutoRechargeSetupSession({ thresholdUsd, topUpUsd, consentAccepted }) {
      const origin = window.location.origin;
      const r = await fetchJson('/payment/stripe/auto-recharge/setup-session', {
        method: 'POST',
        auth: 'always',
        body: {
          thresholdUsd,
          topUpUsd,
          consentAccepted,
          successUrl: `${origin}/?stripe=auto-recharge-setup-success&session={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${origin}/?stripe=auto-recharge-setup-cancel`,
        },
      });
      return r.data;
    },
    async confirmAutoRechargeSetup(sessionId) {
      const r = await fetchJson(`/payment/stripe/auto-recharge/setup-session/${encodeURIComponent(sessionId)}/confirm`, {
        method: 'POST', auth: 'always',
      });
      return r.data;
    },
    async cancelAutoRechargeSetup() {
      const r = await fetchJson('/payment/stripe/auto-recharge/setup-session/cancel', {
        method: 'POST', auth: 'always',
      });
      return r.data;
    },
    async saveAutoRecharge(settings) {
      const r = await fetchJson('/payment/stripe/auto-recharge', {
        method: 'PUT',
        auth: 'always',
        body: settings,
      });
      return r.data;
    },
  };

  // -------- Twitter reward (tweet-for-$1 promo) --------
  // Endpoints (all JWT):
  //   GET  /reward/verification-code  → { code, status, template, expiresAt, rewardAmount, hasClaimedReward }
  //   POST /reward/verify-tweet       → { success, message, rewardAmount?, currentBalance? }
  //   GET  /reward/status             → { status, hasClaimedReward, … }
  // Replaces the legacy /auth/claim/* endpoints.
  const rewardService = {
    async getCode() {
      const r = await fetchJson('/reward/verification-code', { auth: 'always' });
      return r.data;
    },
    async verifyTweet(tweetUrl) {
      const r = await fetchJson('/reward/verify-tweet', { method: 'POST', auth: 'always', body: { tweetUrl } });
      return r.data;
    },
    async status() {
      const r = await fetchJson('/reward/status', { auth: 'always' });
      return r.data;
    },
  };

  // -------- Referral service --------
  const referralService = {
    async info() { const r = await fetchJson('/referral/info', { auth: 'always' }); return r.data; },
    async rewards({ page = 1, limit = 20 } = {}) {
      const r = await fetchJson(`/referral/rewards?page=${page}&limit=${limit}`, { auth: 'always' });
      return r.data || { items: [], total: 0 };
    },
    // 用量返佣明细 —— cursor 分页（无 total，大表友好；合计走 summary()）
    async usageRewards({ cursor, limit = 20 } = {}) {
      const q = new URLSearchParams({ limit: String(limit) });
      if (cursor) q.set('cursor', cursor);
      const r = await fetchJson(`/referral/usage-rewards?${q}`, { auth: 'always' });
      return r.data || { items: [], nextCursor: null };
    },
    // 返佣汇总：余额 + 用量/充值两账本累计（后端缓存 60s）
    async summary() {
      const r = await fetchJson('/referral/summary', { auth: 'always' });
      return r.data || { referralBalance: 0, totalUsageRewards: 0, totalRechargeRewards: 0 };
    },
    async transfer(amount) {
      const r = await fetchJson('/referral/transfer', { method: 'POST', auth: 'always', body: { amount } });
      return r.data;
    },
  };

  // -------- Admin (API review) --------
  const adminService = {
    async pendingReviews(status) {
      const q = status ? `?status=${encodeURIComponent(status)}` : '';
      const r = await fetchJson('/api-services/admin/pending-reviews' + q, { auth: 'always' });
      return r.data || [];
    },
    // Full admin detail — fetched on-demand when admin clicks a row, returns
    // all endpoints with full config, OpenAPI spec, reviews history, provider
    // reputation stats. Backend bypasses the public-visibility guard so
    // PENDING_REVIEW / SANDBOX / DRAFT services are all accessible.
    async serviceFull(apiId) {
      const r = await fetchJson(`/api-services/admin/${encodeURIComponent(apiId)}/full`, { auth: 'always' });
      return r.data;
    },
    async review(apiId, decision, reviewNote) {
      const r = await fetchJson(`/api-services/${apiId}/admin-review`, { method: 'POST', auth: 'always', body: { decision, reviewNote } });
      return r.data;
    },
    async setStatus(apiId, status, reason) {
      const r = await fetchJson(`/api-services/${apiId}/admin-status`, { method: 'PATCH', auth: 'always', body: { status, reason } });
      return r.data;
    },
  };

  // -------- API health metrics (per-API derived from usage_records) --------
  // Backend returns { score24h, avgLatencyMs, currentStatus, lastCheckAt,
  // hourly: 24 slots, daily: 30 slots }. Cached 5min on the server, so polling
  // here on a short interval is fine.
  const apiHealthService = {
    async get(apiServiceId) {
      const r = await fetchJson(`/api-services/${encodeURIComponent(apiServiceId)}/health`, { auth: 'auto' });
      return r.data;
    },
    // Per-endpoint health list — paged + sortable. Used inside HealthDialog
    // to render a virtual-scrolled list when a service has 100+ endpoints.
    // Server caches the full sorted blob (30s); subsequent pages within that
    // window are pure Redis reads (~2ms).
    async endpoints(apiServiceId, { limit = 20, cursor = null, sort = 'worst' } = {}) {
      const q = new URLSearchParams();
      q.set('limit', String(limit));
      q.set('sort', sort);
      if (cursor) q.set('cursor', cursor);
      const r = await fetchJson(
        `/api-services/${encodeURIComponent(apiServiceId)}/health/endpoints?${q}`,
        { auth: 'auto' },
      );
      return r.data || { items: [], nextCursor: null, total: 0 };
    },
    // Single-endpoint detail — lazy-loaded when the user clicks a row in the
    // endpoint list. Returns the same shape as `get()`: 24h hourly + 30d daily.
    async endpoint(apiServiceId, endpointId) {
      const r = await fetchJson(
        `/api-services/${encodeURIComponent(apiServiceId)}/health/endpoints/${encodeURIComponent(endpointId)}`,
        { auth: 'auto' },
      );
      return r.data;
    },
  };

  // -------- Models browser (AI 中转 /ai/models) --------
  // Aggregates models across all ACTIVE+isPublic PER_TOKEN endpoints; the backend
  // groups by model name so each entry carries N providers (mirrors the OpenRouter
  // shape we modeled the page after).
  const modelsService = {
    async list() {
      const r = await fetchJson('/models', { auth: 'auto' });
      return Array.isArray(r.data) ? r.data : [];
    },
    async getBySlug(slug) {
      const r = await fetchJson(`/models/${encodeURIComponent(slug)}`, { auth: 'auto' });
      return r.data;
    },
    // /ai 公开目录：canonical-only，按 vendor 展示，默认按调用量排，带 OpenRouter 折扣。
    async catalog({ window = '30d', limit = 200, offset = 0 } = {}) {
      const p = new URLSearchParams({ window, limit: String(limit), offset: String(offset) });
      const r = await fetchJson(`/models/catalog?${p}`, { auth: 'auto' });
      return r.data || { total: 0, limit, offset, window, items: [] };
    },
  };

  // -------- OAuth (per-key bindings) --------
  const oauthService = {
    async listProviders() { const r = await fetchJson('/oauth/providers', { auth: 'auto' }); return r.data || []; },
    async listBindings() { const r = await fetchJson('/oauth/bindings', { auth: 'always' }); return r.data || []; },
    async authorize(apiKeyId, providerId, scopes) {
      const body = { apiKeyId, providerId };
      if (scopes) body.scopes = scopes;
      const r = await fetchJson('/oauth/authorize', { method: 'POST', auth: 'always', body });
      return r.data; // { authorizationUrl, state }
    },
    async deleteBinding(id) {
      await fetchJson(`/oauth/bindings/${id}`, { method: 'DELETE', auth: 'always' });
      return { id };
    },
  };

  // -------- API service management --------
  const apiSvc = {
    async listMarketplace() { const r = await fetchJson('/api-services', { auth: 'auto' }); return r.data || []; },
    async listMine() { const r = await fetchJson('/api-services/mine', { auth: 'always' }); return r.data || []; },
    async get(id, opts = {}) {
      // asAdmin=true triggers the backend's admin-bypass on non-ACTIVE services
      // (verified server-side against ProviderWhitelist). Used by the admin
      // review flow's "open in detail" button.
      // version='v1.2' scopes currentVersion to a specific revision (owner-only
      // for non-active) — used by the workspace version switcher to load the
      // selected major's working copy / serving revision into the editor.
      const params = [];
      if (opts.asAdmin) params.push('asAdmin=true');
      if (opts.version) params.push(`version=${encodeURIComponent(opts.version)}`);
      const q = params.length ? `?${params.join('&')}` : '';
      const r = await fetchJson(`/api-services/${id}${q}`, { auth: 'auto' });
      return r.data;
    },
    async create(payload) { const r = await fetchJson('/api-services', { method: 'POST', auth: 'always', body: payload }); return r.data; },
    async update(id, payload) { const r = await fetchJson(`/api-services/${id}`, { method: 'PATCH', auth: 'always', body: payload }); return r.data; },
    async delete(id) { await fetchJson(`/api-services/${id}`, { method: 'DELETE', auth: 'always' }); return { id }; },
    // updateBasic / updateVersion / patchVersion / createVersion / listVersions
    //   PATCH /api-services/:id                           — basic info only
    //   PUT   /api-services/:id/versions/:versionId       — full replace (editor save)
    //   PATCH /api-services/:id/versions/:versionId       — partial update (only sent fields)
    //   POST  /api-services/:id/versions  + makeActive    — release a new version
    async updateBasic(id, body) { const r = await fetchJson(`/api-services/${id}`, { method: 'PATCH', auth: 'always', body }); return r.data; },
    async getSandboxBinding(id) { const r = await fetchJson(`/api-services/${id}/sandbox`, { auth: 'always' }); return r.data; },
    async upsertSandboxBinding(id, body) { const r = await fetchJson(`/api-services/${id}/sandbox`, { method: 'PUT', auth: 'always', body }); return r.data; },
    // 注册前探测：贴上上游地址就能认出这是不是 Sandbox Adapter（manifest 公开，无需 token）。
    async probeSandboxAdapter(baseUrl) { const r = await fetchJson('/api-services/sandbox/probe', { method: 'POST', auth: 'always', body: { baseUrl } }); return r.data; },
    // 消费者 console：我的 Sandbox 实例（JWT 直查，不需要 API key）。
    async sandboxInstances(params) { const r = await fetchJson('/sandbox-consumer/instances', { auth: 'always', params }); return r.data; },
    async sandboxInstance(id) { const r = await fetchJson(`/sandbox-consumer/instances/${id}`, { auth: 'always' }); return r.data; },
    async sandboxInstanceAudit(id, params) { const r = await fetchJson(`/sandbox-consumer/instances/${id}/audit`, { auth: 'always', params }); return r.data; },
    async sandboxOperations(params) { const r = await fetchJson('/sandbox-consumer/operations', { auth: 'always', params }); return r.data; },
    async sandboxMetrics(params) { const r = await fetchJson('/sandbox-consumer/metrics', { auth: 'always', params }); return r.data; },
    async verifySandboxBinding(id) { const r = await fetchJson(`/api-services/${id}/sandbox/verify`, { method: 'POST', auth: 'always', body: {} }); return r.data; },
    async disableSandboxBinding(id) { const r = await fetchJson(`/api-services/${id}/sandbox`, { method: 'DELETE', auth: 'always' }); return r.data; },
    /** Owner skill authoring context (endpoints, scaffold SKILL.md, drift). */
    async skillContext(id) {
      const r = await fetchJson(`/api-services/${id}/skill-context`, { auth: 'always' });
      return r.data;
    },
    /** Persist skill↔API endpoint fingerprint after package submit. */
    async updateSkillFingerprint(id, body) {
      const r = await fetchJson(`/api-services/${id}/skill-fingerprint`, {
        method: 'PUT',
        auth: 'always',
        body: body || {},
      });
      return r.data;
    },
    async listVersions(id) { const r = await fetchJson(`/api-services/${id}/versions`, { auth: 'auto' }); return r.data || []; },
    async updateVersion(id, versionId, body) { const r = await fetchJson(`/api-services/${id}/versions/${versionId}`, { method: 'PUT', auth: 'always', body }); return r.data; },
    async patchVersion(id, versionId, body) { const r = await fetchJson(`/api-services/${id}/versions/${versionId}`, { method: 'PATCH', auth: 'always', body }); return r.data; },
    async createVersion(id, body) { const r = await fetchJson(`/api-services/${id}/versions`, { method: 'POST', auth: 'always', body }); return r.data; },
    // ── 版本/状态生命周期（feat/version-lifecycle）──
    //   大版本聚合视图 / diff 预览 / 新建大版本 / 切默认 / 回滚 / 拉工作副本 / 申请上线
    async versionOverview(id) { const r = await fetchJson(`/api-services/${id}/version-overview`, { auth: 'always' }); return r.data; },
    async diffPreview(id, major) { const r = await fetchJson(`/api-services/${id}/majors/${major}/diff-preview`, { auth: 'always' }); return r.data; },
    // 某修订的完整审核报告（diff + verdict + 测试用例 + 问题清单 + 稳定性 + transcript + 元信息）。
    async revisionReview(id, revId) { const r = await fetchJson(`/api-services/${id}/revisions/${revId}/review`, { auth: 'always' }); return r.data; },
    async createMajor(id) { const r = await fetchJson(`/api-services/${id}/majors`, { method: 'POST', auth: 'always' }); return r.data; },
    async setDefaultMajor(id, major) { const r = await fetchJson(`/api-services/${id}/default-major`, { method: 'PUT', auth: 'always', body: { major } }); return r.data; },
    async rollbackMajor(id, major, revisionId, reason) { const r = await fetchJson(`/api-services/${id}/majors/${major}/rollback`, { method: 'POST', auth: 'always', body: { revisionId, reason } }); return r.data; },
    async setMajorDeprecated(id, major, deprecated) { const r = await fetchJson(`/api-services/${id}/majors/${major}/deprecated`, { method: 'PUT', auth: 'always', body: { deprecated } }); return r.data; },
    async startEdit(id, major) { const r = await fetchJson(`/api-services/${id}/majors/${major}/working-revision`, { method: 'POST', auth: 'always' }); return r.data; },
    // 申请上线唯一正路：提交审核（过 diff 触发门 → 自动审核 / 直接上线）。旧 publishRevision
    // （跳过审核的 /publish）已随后端路由一并退役。
    async submitRevision(id, revId, body) { const r = await fetchJson(`/api-services/${id}/revisions/${revId}/submit`, { method: 'POST', auth: 'always', body: body || {} }); return r.data; },
    // V2 — UPSTREAM_POLLING upstream provider list. Backend currently hard-codes
    // KNOWN_UPSTREAM_PROVIDERS = new Set(['byteplus', 'openrouter']) in api.service.ts.
    // When the backend ships a GET /api-services/known-providers endpoint, replace the
    // hardcode here without touching call sites in register.jsx.
    async knownUpstreamProviders() {
      return ['byteplus', 'openrouter'];
    },
    async parseOpenApi(content) {
      // Backend expects { content: string }  (per xapi-new src/services/apiService.ts:435).
      const body = { content: typeof content === 'string' ? content : JSON.stringify(content) };
      const r = await fetchJson('/api-services/parse-openapi', { method: 'POST', auth: 'always', body });
      return r.data;
    },
    async importOpenApi(payload) {
      const r = await fetchJson('/api-services/import/openapi', { method: 'POST', auth: 'always', body: payload });
      return r.data;
    },
    // 项目创建模板（弹窗用）。listTemplates 只返回轻量元数据；getTemplate 拉完整 openApiSpec。
    async listTemplates() {
      const r = await fetchJson('/project-templates', { auth: 'always' });
      return r.data || [];
    },
    async getTemplate(slug) {
      const r = await fetchJson(`/project-templates/${slug}`, { auth: 'always' });
      return r.data;
    },
    async createTemplate(payload) {
      const r = await fetchJson('/project-templates', { method: 'POST', auth: 'always', body: payload });
      return r.data;
    },
    async submitForReview(id, note) {
      const r = await fetchJson(`/api-services/${id}/submit-review`, { method: 'POST', auth: 'always', body: { note } });
      return r.data;
    },
    // DYNAMIC billing: authoritative rule validation (same validateRulesConfig the
    // gateway prices with). Body { dynamicRules }, returns { ok, errors:[{message,rule?,field?}] }.
    async validateDynamicRules(body) {
      const r = await fetchJson('/api-services/validate-dynamic-rules', { method: 'POST', auth: 'always', body });
      return r.data;
    },
    // Host 前缀实时校验（不创建/不修改）：复用与写入路径同一份保留词/黑名单/长度校验 +
    // 唯一性检查。Body { host, excludeId? } → { ok, available, normalized, code?, message? }。
    // 校验非权威，最终仍以创建/更新为准；throttle 120/min，前端防抖后调用。
    async validateHost(host, excludeId) {
      const r = await fetchJson('/api-services/host/validate', {
        method: 'POST',
        auth: 'always',
        body: excludeId ? { host, excludeId } : { host },
      });
      return r.data;
    },
    // DYNAMIC billing presets (openai / claude / gemini / video) → [{ key, label, description, aiInterfaceSpecId, spec }].
    async getDynamicTemplates() {
      const r = await fetchJson('/api-services/dynamic-templates', { auth: 'always' });
      return r.data || [];
    },
    // AI-interface detection (path sniff + billing hint). Body { path, billingType? } →
    // { suggested:{ specId, confidence, detectedBy }, pathMatch, billingHint }. Drives the
    // 模型 tab gating: suggested.specId != null ⇒ AI interface. (EntityAccountGuard.)
    async detectAiEndpoint(body) {
      const r = await fetchJson('/v1/ai-spec/detect-endpoint', { method: 'POST', auth: 'always', body });
      return r.data;
    },
    // Registered interface specs (public) — label/source for the detected spec chip.
    async listInterfaceSpecs() {
      const r = await fetchJson('/v1/ai-spec/interface-specs', { auth: 'auto' });
      return r.data || [];
    },
    // WebSocket adapters (JWT) — drives the WS endpoint form's adapter dropdown.
    // Backend GET /ws/adapters → { adapters: [{ name, label, clientProtocol,
    // requiredWsConfig, description, docUrl }] }. Returns the adapters array
    // (empty on 403/404 so the form degrades gracefully instead of throwing).
    async getWsAdapters() {
      try {
        const r = await fetchJson('/ws/adapters', { auth: 'always' });
        return (r.data && r.data.adapters) || [];
      } catch (e) {
        if (e && (e.status === 403 || e.status === 404)) return [];
        throw e;
      }
    },
  };

  // -------- Gateway invocation --------
  // Builds upstream URL from API host + endpoint path with path-param substitution,
  // appends queryParams, attaches XAPI-KEY header. Streaming via ReadableStream when streaming=true.
  const gateway = {
    buildUrl(host, path, pathParams = {}, queryParams = {}) {
      let p = path || '/';
      Object.keys(pathParams || {}).forEach((k) => {
        const v = encodeURIComponent(pathParams[k] ?? '');
        p = p.replace(new RegExp(':' + k + '(?=/|$)', 'g'), v).replace(new RegExp('\\{' + k + '\\}', 'g'), v);
      });
      const q = Object.entries(queryParams || {})
        .filter(([, v]) => v !== '' && v !== null && v !== undefined)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
      const base = buildGatewayUrl(host);
      const url = base + p + (q ? (p.includes('?') ? '&' : '?') + q : '');
      return url;
    },

    async invoke({ host, method, path, pathParams, queryParams, headers, body, apiKey, signal, streaming, onChunk, async, idempotencyKey }) {
      const url = this.buildUrl(host, path, pathParams, queryParams);
      const h = { ...(headers || {}) };
      // Set ONE casing only — HTTP headers are case-insensitive, but if both are sent the server
      // may concatenate them into "key,key" and the lookup fails. Backend reads `xapi-key` (lowercase).
      if (apiKey) h['xapi-key'] = apiKey;
      // Async mode — backend gateway routes to TaskService when this header is true on an
      // OPTIONAL endpoint, and always for REQUIRED. Backend reads lowercase, set lowercase.
      if (async) h['x-xapi-async'] = 'true';
      if (idempotencyKey) h['idempotency-key'] = idempotencyKey;
      const init = { method: (method || 'GET').toUpperCase(), headers: h, signal };
      if (body !== undefined && body !== null && init.method !== 'GET' && init.method !== 'HEAD') {
        if (typeof FormData !== 'undefined' && body instanceof FormData) {
          // multipart/form-data（含文件）：直接给 FormData，绝不能手动设 Content-Type，
          // 让浏览器自动带 boundary，否则上游解析失败。
          init.body = body;
          delete h['Content-Type'];
          delete h['content-type'];
        } else {
          init.body = typeof body === 'string' ? body : JSON.stringify(body);
          if (!h['Content-Type'] && !h['content-type']) h['Content-Type'] = 'application/json';
        }
      }
      const t0 = performance.now();
      let res;
      try { res = await fetch(url, init); }
      catch (e) {
        return { ok: false, error: 'network: ' + (e && e.message ? e.message : e), latency: Math.round(performance.now() - t0) };
      }
      const respHeaders = {};
      res.headers.forEach((v, k) => { respHeaders[k] = v; });

      if (streaming && res.body && (res.headers.get('content-type') || '').includes('text/event-stream')) {
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        let combined = '';
        for (;;) {
          let chunk;
          try { chunk = await reader.read(); } catch (e) { break; }
          if (chunk.done) break;
          const text = dec.decode(chunk.value, { stream: true });
          buf += text;
          combined += text;
          const lines = buf.split('\n');
          buf = lines.pop() || '';
          for (const line of lines) {
            const t = line.trim();
            if (!t) continue;
            if (t === 'data: [DONE]') { onChunk && onChunk('', { done: true }); continue; }
            if (t.startsWith('data: ')) {
              try {
                const json = JSON.parse(t.slice(6));
                let piece = '';
                // OpenAI-style
                if (json.choices && json.choices[0] && json.choices[0].delta) {
                  piece = json.choices[0].delta.content || '';
                }
                // Anthropic-style
                else if (json.delta && (json.delta.type === 'text_delta' || json.delta.text)) {
                  piece = json.delta.text || '';
                }
                if (piece && onChunk) onChunk(piece, { json });
              } catch {}
            }
          }
        }
        const latency = Math.round(performance.now() - t0);
        return { ok: res.ok, status: res.status, headers: respHeaders, body: combined, streaming: true, latency };
      }

      // Non-streaming. Decode by content-type:
      //  - JSON (incl. +json suffixes)        → parsed object
      //  - known textual types / no type      → string
      //  - everything else (audio/*, image/*, video/*, octet-stream, pdf…) → Blob
      // Binary bodies MUST go through res.blob(): res.text() UTF-8-decodes them and
      // irreversibly mangles the bytes (an MP3/PNG turns into garbage). The Try-it UI
      // sniffs `body instanceof Blob` to render <audio>/<img>/<video>/download.
      const ct = res.headers.get('content-type') || '';
      const isTextual = !ct || ct.includes('json') || ct.startsWith('text/')
        || ct.includes('xml') || ct.includes('x-www-form-urlencoded');
      let data;
      if (ct.includes('json')) {
        try { data = await res.json(); } catch { data = null; }
      } else if (isTextual) {
        try { data = await res.text(); } catch { data = null; }
      } else {
        try { data = await res.blob(); } catch { data = null; }
      }
      const latency = Math.round(performance.now() - t0);
      // Async submission lands as 202 with { task_id, status: 'pending', poll_url, expires_at }.
      // Surface it as a distinct shape so the caller can branch into polling without sniffing
      // status codes. We still return the raw body for debugging.
      if (res.status === 202 && data && data.task_id) {
        return {
          ok: true, status: 202, headers: respHeaders, latency,
          async: true,
          taskId: data.task_id,
          taskStatus: data.status || 'pending',
          pollUrl: data.poll_url || `/v1/tasks/${data.task_id}`,
          expiresAt: data.expires_at,
          body: data,
        };
      }
      return { ok: res.ok, status: res.status, headers: respHeaders, body: data, latency };
    },
  };

  // -------- Aggregated AI Gateway --------
  // The browser chooses a canonical model and strategy. Provider selection, fallback and
  // protocol translation stay behind the central AI gateway.
  const aiGateway = {
    buildUrl(path = '/') {
      const base = String(cfg.aiGatewayBase || '').replace(/\/+$/, '');
      return base + (path.startsWith('/') ? path : `/${path}`);
    },

    async request({ path, method = 'GET', apiKey, body, signal }) {
      const headers = {};
      if (apiKey) headers['xapi-key'] = apiKey;
      const init = { method, headers, signal };
      if (body !== undefined && body !== null && method !== 'GET' && method !== 'HEAD') {
        headers['Content-Type'] = 'application/json';
        init.body = typeof body === 'string' ? body : JSON.stringify(body);
      }
      const startedAt = performance.now();
      let response;
      try { response = await fetch(this.buildUrl(path), init); }
      catch (error) {
        if (error?.name === 'AbortError') throw error;
        return { ok: false, error: `network: ${error?.message || error}`, latency: Math.round(performance.now() - startedAt) };
      }
      const responseHeaders = {};
      response.headers.forEach((value, key) => { responseHeaders[key] = value; });
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => null);
      return {
        ok: response.ok, status: response.status, headers: responseHeaders, body: data,
        latency: Math.round(performance.now() - startedAt),
      };
    },

    async listResponseModels({ signal } = {}) {
      const query = new URLSearchParams({
        interface: 'openai_responses', input: 'text', output: 'text',
      });
      const result = await this.request({ path: `/v1/models?${query}`, signal });
      if (!result.ok) {
        throw new Error(apiErrorMessage(result.body, result.error || `HTTP ${result.status || 'error'}`));
      }
      return Array.isArray(result.body?.data) ? result.body.data : [];
    },

    async createResponse({ strategy = 'default', apiKey, body, signal }) {
      if (!['default', 'quality', 'speed', 'cost'].includes(strategy)) {
        throw new Error(`unsupported AI Gateway strategy: ${strategy}`);
      }
      const route = strategy === 'default' ? '' : `/${strategy}`;
      return this.request({
        path: `${route}/v1/responses`, method: 'POST', apiKey, body, signal,
      });
    },
  };

  // -------- Task service (async tasks) --------
  // Backend exposes /v1/tasks/* on the proxy app's gateway controller. Both endpoints are
  // gated by ApiKeyAuthGuard and tasks are isolated by apiKey, so every call must carry an
  // xapi-key header. Host doesn't affect routing (catch-all on gateway), but a real host
  // must be passed so DNS resolves — callers should pass the user's currently-selected
  // service host (or any host they own).
  const taskService = {
    async _fetch(host, path, apiKey, init = {}) {
      const url = buildGatewayUrl(host) + path;
      const headers = { 'xapi-key': apiKey, ...(init.headers || {}) };
      const res = await fetch(url, { ...init, headers });
      const ct = res.headers.get('content-type') || '';
      const data = ct.includes('application/json')
        ? await res.json().catch(() => null)
        : await res.text().catch(() => null);
      return { ok: res.ok, status: res.status, data };
    },

    // GET /v1/tasks/:taskId — returns 200 with { task_id, status, ... } regardless of state.
    // The HTTP `Retry-After` header on pending/processing responses (seconds, integer)
    // is the backend's recommended next-poll delay. Returning it lets callers tune
    // their polling cadence; absence => caller picks a default (see schedulePoll).
    async poll(host, taskId, apiKey) {
      if (!taskId || !apiKey) throw new Error('taskId and apiKey are required');
      const url = buildGatewayUrl(host) + `/v1/tasks/${encodeURIComponent(taskId)}`;
      const res = await fetch(url, { headers: { 'xapi-key': apiKey } });
      const ct = res.headers.get('content-type') || '';
      const data = ct.includes('application/json')
        ? await res.json().catch(() => null)
        : await res.text().catch(() => null);
      const retryAfterRaw = res.headers.get('Retry-After');
      const retryAfter = retryAfterRaw && /^\d+$/.test(retryAfterRaw)
        ? Math.max(1, parseInt(retryAfterRaw, 10))
        : null;
      if (res.status === 404) return null;
      if (!res.ok) throw new Error((data && data.error && data.error.message) || `Poll failed: HTTP ${res.status}`);
      // Decorate data with non-enumerable retryAfter so existing callers that just
      // read data.status / data.result keep working; new callers can read it.
      if (data && retryAfter != null) {
        Object.defineProperty(data, '_retryAfter', { value: retryAfter, enumerable: false });
      }
      return data;
    },

    // GET /v1/tasks?status=...&limit=&cursor=
    async list(host, apiKey, { status, limit, cursor } = {}) {
      if (!apiKey) throw new Error('apiKey is required');
      const q = new URLSearchParams();
      if (status) q.set('status', Array.isArray(status) ? status.join(',') : status);
      if (limit) q.set('limit', String(limit));
      if (cursor) q.set('cursor', cursor);
      const qs = q.toString();
      const r = await this._fetch(host, '/v1/tasks' + (qs ? `?${qs}` : ''), apiKey);
      if (!r.ok) throw new Error((r.data && r.data.error && r.data.error.message) || `List failed: HTTP ${r.status}`);
      return r.data || { tasks: [], next_cursor: null, has_more: false };
    },

    // ---------- JWT-authed equivalents ----------
    // Backed by the API app's `/user/tasks`, scoped to the signed-in user
    // across every API key they own. The console Tasks tab uses these so the
    // user just sees their full async history without setting XAPI_KEY first.
    // Both virtual and entity users can call them; the backend filters by
    // jwt-userId so each user only ever sees their own tasks.
    async listMine({ status, limit, cursor } = {}) {
      const q = new URLSearchParams();
      if (status) q.set('status', Array.isArray(status) ? status.join(',') : status);
      if (limit) q.set('limit', String(limit));
      if (cursor) q.set('cursor', cursor);
      const qs = q.toString();
      const r = await fetchJson('/user/tasks' + (qs ? `?${qs}` : ''), { auth: 'always' });
      return r.data || { tasks: [], next_cursor: null, has_more: false };
    },
    async pollMine(taskId) {
      if (!taskId) throw new Error('taskId is required');
      try {
        const r = await fetchJson(`/user/tasks/${encodeURIComponent(taskId)}`, { auth: 'always' });
        // fetchJson doesn't currently surface Retry-After. The Console TasksTab uses
        // a fixed 5s interval which is fine for the JWT-authed path. If we later
        // need per-task pacing here, plumb headers through fetchJson.
        return r.data;
      } catch (e) {
        if (e && e.status === 404) return null;
        throw e;
      }
    },
  };

  // -------- Adapters: backend shapes -> existing v2 UI shapes --------
  const adapters = {
    user(u) {
      if (!u) return null;
      const handle =
        u.twitterScreenName ||
        (u.address ? (u.address.slice(0, 6) + '…' + u.address.slice(-4)) : ('user-' + String(u.id || '').slice(0, 4)));
      const balance = parseFloat(u.balance || '0') || 0;
      // 服务商 API 收益余额（仅它可提现，独立于充值/赠送 balance）。顶栏据此拆开展示。
      const earningsBalance = parseFloat(u.earningsBalance || '0') || 0;
      return {
        handle,
        accountType: u.accountType || 'VIRTUAL',
        balance,
        earningsBalance,
        canProvideApis: !!u.canProvideApis,
        avatar: null,
        _raw: u,
      };
    },

    apiKey(k) {
      if (!k) return null;
      // Important: distinguish balanceLimit=0 (cap, no spend) from null (truly unlimited).
      // Merged-from-virtual keys arrive with limit=0 to protect the entity's main balance.
      const limit = (k.balanceLimit != null) ? parseFloat(k.balanceLimit) : null;
      const used = parseFloat(k.usedBalance || '0') || 0;
      return {
        id: k.id,
        name: k.name,
        preview: k.keyPreview,
        created: (k.createdAt || '').slice(0, 10),
        lastUsed: k.lastUsedAt ? formatRelative(k.lastUsedAt) : 'never',
        usage: k.totalUsageRecords || 0,
        limit: (limit != null && !isNaN(limit)) ? limit : null,
        used,
        sandbox: !!k.allowSandbox,
        register: !!k.allowRegister,
        scopes: Array.isArray(k.scopes) ? k.scopes : [],
        workerAccessMode: k.workerAccessMode || 'ALL',
        workerAccessIds: Array.isArray(k.workerAccessIds) ? k.workerAccessIds : [],
        allowedCidrs: Array.isArray(k.allowedCidrs) ? k.allowedCidrs : [],
        oauth: !!k.oauthEnabled,
        isDefault: false,
        _raw: k,
      };
    },

    apiService(a) {
      if (!a) return null;
      // Backend findMine returns: activeVersion = "v1.0" (string) | latestVersion = "v1.0" | versions[0].version
      // Backend findOne returns:  activeVersion = { version, id } (object) | currentVersion.version
      const versionStr =
        (typeof a.activeVersion === 'string' && a.activeVersion) ||
        (typeof a.latestVersion === 'string' && a.latestVersion) ||
        (a.activeVersion && a.activeVersion.version) ||
        (a.currentVersion && a.currentVersion.version) ||
        (Array.isArray(a.versions) && a.versions[0] && a.versions[0].version) ||
        '—';
      // Preserve the source host; display the same environment-aware authority used by calls.
      const rawHost = a.host || '';
      const prefix = a.hostPrefix || rawHost.split('.')[0] || '';
      const displayHost = gatewayHost(rawHost || prefix, a.integrationMode) || (a.baseUrl || '').replace(/^https?:\/\//, '').split('/')[0] || '';
      // 收集 endpoint 列表，用来算 AI badge / pending 计数。
      const epList =
        (Array.isArray(a.endpoints) ? a.endpoints : null) ||
        (a.activeVersion && Array.isArray(a.activeVersion.endpoints) ? a.activeVersion.endpoints : null) ||
        (a.currentVersion && Array.isArray(a.currentVersion.endpoints) ? a.currentVersion.endpoints : null) ||
        [];
      // 方案 X 派生：AI endpoint 数 + pending mappings 总数（canonicalId=null 的行）。
      let aiEndpointCount = 0;
      let pendingCount = 0;
      for (const ep of epList) {
        if (ep && ep.aiInterfaceSpecId) {
          aiEndpointCount++;
          const mappings = Array.isArray(ep.aiModelMappings) ? ep.aiModelMappings : [];
          for (const m of mappings) {
            if (!m || !m.canonicalId) pendingCount++;
          }
        }
      }
      return {
        id: a.id,
        name: a.name,
        host: displayHost,
        rawHost,
        hostPrefix: prefix,
        // 工作台状态以修订生命周期为准:后端 findMine 现在派生 lifecycleState（基于
        // ApiVersion.state+isServing,见后端注释)。回退到旧 ApiService.status 以兼容未升级的后端。
        status: a.lifecycleState || a.status || 'DRAFT',
        category: a.category || '—',
        version: versionStr,
        endpoints:
          (Array.isArray(a.endpoints) ? a.endpoints.length : null) ??
          (a.endpointsCount != null ? a.endpointsCount : null) ??
          (a.activeVersion && Array.isArray(a.activeVersion.endpoints) ? a.activeVersion.endpoints.length : null) ??
          (a.currentVersion && Array.isArray(a.currentVersion.endpoints) ? a.currentVersion.endpoints.length : null) ??
          0,
        // 方案 X: 额外暴露的 AI 派生信息
        aiEndpointCount,
        pendingCount,
        calls30d: a.calls30d || 0,
        earnings30d: a.earnings30d || 0,
        _raw: a,
      };
    },
  };

  function formatRelative(iso) {
    try {
      const t = new Date(iso).getTime();
      const d = Date.now() - t;
      if (d < 60_000) return 'just now';
      if (d < 3_600_000) return Math.floor(d / 60_000) + 'm ago';
      if (d < 86_400_000) return Math.floor(d / 3_600_000) + 'h ago';
      if (d < 30 * 86_400_000) return Math.floor(d / 86_400_000) + 'd ago';
      return iso.slice(0, 10);
    } catch { return ''; }
  }

  return {
    config: cfg, tokens, userKeyStash, fetchJson,
    authService, keyService, apiSvc, gateway, aiGateway, adapters,
    usageService, aiLogsService, statsService, favoriteService, subscriptionService, stripeService, rewardService, referralService, adminService, oauthService, agentService, modelsService, apiHealthService,
    actionsService, skillService, x402Service, providerDashboardService, withdrawalService, earningsTransferService, taskService,
    helpers: { buildGatewayUrl, buildWsGatewayUrl, gatewayHost, gatewaySuffix, buildWsAuthProtocols, buildWsCredentialProtocols, apiSlug, isLocalDev, formatRelative, isAuthBlocked: isAuthRefreshBlocked, clearAuthBlock: clearAuthRefreshBlock, refreshAccessToken: doRefresh },
  };
})();

// Legacy alias for inline handlers/3rd-party scripts that still reference window.XApi.
if (typeof window !== 'undefined') window.XApi = XApi;
